import type { TranslationProvider } from '@ports/TranslationProvider.ts';
import { messageForTranslationFailure, TranslationError } from './translationFailure.ts';

export type GoogleTranslationProviderOptions = {
  fetchImpl?: typeof fetch;
};

/**
 * Free-tier Google translate — same as extension translationLegacyBridge `google()`:
 * POST form body to translate.googleapis.com (client-side gtx). No backend proxy.
 */
export class GoogleTranslationProvider implements TranslationProvider {
  private readonly fetchImpl: typeof fetch;

  constructor(options: GoogleTranslationProviderOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  async translate(texts: string[], targetLanguage: string): Promise<string[]> {
    if (texts.length === 0) return [];

    const data = new URLSearchParams();
    for (const text of texts) {
      data.append('q', text);
    }

    let res: Response;
    try {
      res = await this.fetchImpl(
        `https://translate.googleapis.com/translate_a/t?client=gtx&dt=t&sl=auto&tl=${encodeURIComponent(targetLanguage)}`,
        {
          method: 'POST',
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: data,
        },
      );
    } catch {
      throw new TranslationError('failed', 'Google translate network error');
    }

    if (!res.ok) {
      throw new TranslationError('failed', `Google translate failed: ${res.status}`);
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new TranslationError('failed', 'Google translate invalid JSON');
    }

    // Extension: json.map((t) => ({ translations: [{ text: t[0] }] }))
    if (!Array.isArray(json)) {
      throw new TranslationError('failed', 'Invalid google translate response');
    }

    return texts.map((_, i) => {
      const row = json[i];
      if (Array.isArray(row) && row.length > 0) {
        const text = row[0];
        return typeof text === 'string' ? text : messageForTranslationFailure('failed');
      }
      return messageForTranslationFailure('failed');
    });
  }
}
